import React, { useEffect, useState } from "react";
import * as Yup from "yup";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { ValidationRuleTypeEnum } from "../../Enum";
import { manResponseType } from "../../interfaces";
import { associateValueToRule } from "../../Services/ChangeService";
import AutocompleteInput from "../AutocompleteInput/AutocompleteInput";
import PhaseSelector from "../PhaseSelector/PhaseSelector";

type CausalityRuleFormProps = {
  onSubmit: (data: any) => void;
  manifestNames: Record<string, string>;
  responseDescriptions: manResponseType[];
  idManResponseAvailable: number[];
};

const CausalityRuleForm: React.FC<CausalityRuleFormProps> = ({
  onSubmit,
  manifestNames,
  responseDescriptions,
  idManResponseAvailable,
}) => {
  const validationSchema = Yup.object().shape({
    firstVariableName: Yup.string().required("First variable is required"),
    firstCondition: Yup.number()
      .typeError("First condition must be selected")
      .required("First condition is required"),
    firstValue: Yup.string().required("First value is required"),
    secondVariableName: Yup.string().required("Second variable is required"),
    secondCondition: Yup.number()
      .typeError("Second condition must be selected")
      .required("Second condition is required"),
    secondValue: Yup.string().when("useFirstVariableValue", {
      is: false,
      then: (schema) => schema.required("Second value is required"),
    }),
    errorMessage: Yup.string().required("Error message is required"),
    applicablePhaseIds: Yup.array().of(Yup.number()),
    useFirstVariableValue: Yup.boolean(),
  });

  const {
    register,
    handleSubmit,
    control,
    watch,
    formState: { errors },
    reset,
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      firstVariableName: "",
      firstCondition: 0,
      firstValue: "",
      secondVariableName: "",
      secondCondition: 0,
      secondValue: "",
      errorMessage: "",
      applicablePhaseIds: [],
      useFirstVariableValue: false,
    },
  });

  const useFirstVariableValue = watch("useFirstVariableValue");
  const watchedFirstVariable = watch("firstVariableName");
  const watchedSecondVariable = watch("secondVariableName");
  const selectedPhases = watch("applicablePhaseIds") as number[];

  const [firstOptions, setFirstOptions] = useState<manResponseType[]>([]);
  const [secondOptions, setSecondOptions] = useState<manResponseType[]>([]);
  const [isLoadingFirstOptions, setIsLoadingFirstOptions] = useState(false);
  const [isLoadingSecondOptions, setIsLoadingSecondOptions] = useState(false);

  // Load options for first variable
  useEffect(() => {
    const fetchFirstOptions = async () => {
      if (!watchedFirstVariable) {
        setFirstOptions([]);
        return;
      }
      if (watchedFirstVariable.toString().endsWith("1")) {
        setFirstOptions([]);
        return;
      }

      setIsLoadingFirstOptions(true);
      try {
        const res = await associateValueToRule(
          "",
          watchedFirstVariable.toString(),
          [],
          idManResponseAvailable,
          responseDescriptions
        );
        setFirstOptions(
          responseDescriptions.filter((desc) => desc.Id === res[1])
        );
      } catch {
        setFirstOptions(
          responseDescriptions.filter(
            (desc) => Number(desc.Id) === Number(watchedFirstVariable)
          )
        );
      } finally {
        setIsLoadingFirstOptions(false);
      }
    };
    fetchFirstOptions();
  }, [watchedFirstVariable, idManResponseAvailable, responseDescriptions]);

  // Load options for second variable
  useEffect(() => {
    const fetchSecondOptions = async () => {
      if (!watchedSecondVariable) {
        setSecondOptions([]);
        return;
      }
      if (watchedSecondVariable.toString().endsWith("1")) {
        setSecondOptions([]);
        return;
      }

      setIsLoadingSecondOptions(true);
      try {
        const res = await associateValueToRule(
          "",
          watchedSecondVariable.toString(),
          [],
          idManResponseAvailable,
          responseDescriptions
        );
        setSecondOptions(
          responseDescriptions.filter((desc) => desc.Id === res[1])
        );
      } catch {
        setSecondOptions(
          responseDescriptions.filter(
            (desc) => Number(desc.Id) === Number(watchedSecondVariable)
          )
        );
      } finally {
        setIsLoadingSecondOptions(false);
      }
    };
    fetchSecondOptions();
  }, [watchedSecondVariable, idManResponseAvailable, responseDescriptions]);

  const handleFormSubmit = (data: any) => {
    onSubmit(data);
    reset();
    setFirstOptions([]);
    setSecondOptions([]);
  };

  const handleReset = () => {
    reset();
    setFirstOptions([]);
    setSecondOptions([]);
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
      <h3 className="text-xl font-semibold text-gray-800 mb-4">
        Create Causality Rule
      </h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* First Variable */}
        <Controller
          name="firstVariableName"
          control={control}
          render={({ field }) => (
            <AutocompleteInput
              id="firstVariableName"
              label="First Variable"
              placeholder="Search for first variable..."
              manifestNames={manifestNames}
              value={field.value}
              onChange={(name, id) => field.onChange(id)}
              error={errors.firstVariableName?.message}
              required
            />
          )}
        />

        {/* Second Variable */}
        <Controller
          name="secondVariableName"
          control={control}
          render={({ field }) => (
            <AutocompleteInput
              id="secondVariableName"
              label="Second Variable"
              placeholder="Search for second variable..."
              manifestNames={manifestNames}
              value={field.value}
              onChange={(name, id) => field.onChange(id)}
              error={errors.secondVariableName?.message}
              required
            />
          )}
        />

        {/* First Condition */}
        <div>
          <label
            htmlFor="firstCondition"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            First Condition <span className="text-red-500">*</span>
          </label>
          <select
            id="firstCondition"
            className={`w-full border rounded-lg p-2.5 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
              errors.firstCondition ? "border-red-500" : "border-gray-300"
            }`}
            {...register("firstCondition")}
          >
            <option value="">Select condition...</option>
            <option value={ValidationRuleTypeEnum.Required}>Required</option>
            <option value={ValidationRuleTypeEnum.MinValue}>Min Value</option>
            <option value={ValidationRuleTypeEnum.MaxValue}>Max Value</option>
            <option value={ValidationRuleTypeEnum.MinLength}>Min Length</option>
            <option value={ValidationRuleTypeEnum.MaxLength}>Max Length</option>
            <option value={ValidationRuleTypeEnum.DateAfter}>Date After</option>
            <option value={ValidationRuleTypeEnum.DateBefore}>Date Before</option>
            <option value={ValidationRuleTypeEnum.EqualTo}>Equal To</option>
            <option value={ValidationRuleTypeEnum.NotEqualTo}>Not Equal To</option>
          </select>
          {errors.firstCondition && (
            <p className="text-red-500 text-xs mt-1">
              {errors.firstCondition.message}
            </p>
          )}
        </div>

        {/* Second Condition */}
        <div>
          <label
            htmlFor="secondCondition"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            Second Condition <span className="text-red-500">*</span>
          </label>
          <select
            id="secondCondition"
            className={`w-full border rounded-lg p-2.5 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
              errors.secondCondition ? "border-red-500" : "border-gray-300"
            }`}
            {...register("secondCondition")}
          >
            <option value="">Select condition...</option>
            <option value={ValidationRuleTypeEnum.Required}>Required</option>
            <option value={ValidationRuleTypeEnum.MinValue}>Min Value</option>
            <option value={ValidationRuleTypeEnum.MaxValue}>Max Value</option>
            <option value={ValidationRuleTypeEnum.MinLength}>Min Length</option>
            <option value={ValidationRuleTypeEnum.MaxLength}>Max Length</option>
            <option value={ValidationRuleTypeEnum.DateAfter}>Date After</option>
            <option value={ValidationRuleTypeEnum.DateBefore}>Date Before</option>
            <option value={ValidationRuleTypeEnum.EqualTo}>Equal To</option>
            <option value={ValidationRuleTypeEnum.NotEqualTo}>Not Equal To</option>
          </select>
          {errors.secondCondition && (
            <p className="text-red-500 text-xs mt-1">
              {errors.secondCondition.message}
            </p>
          )}
        </div>

        {/* First Value */}
        <div>
          <label
            htmlFor="firstValue"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            First Value <span className="text-red-500">*</span>
          </label>
          {isLoadingFirstOptions ? (
            <div className="w-full border border-gray-300 rounded-lg p-2.5 bg-gray-50 flex items-center justify-center">
              <span className="text-gray-500 text-sm">Loading options...</span>
            </div>
          ) : firstOptions.length > 0 ? (
            <select
              id="firstValue"
              {...register("firstValue")}
              className={`w-full border rounded-lg p-2.5 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                errors.firstValue ? "border-red-500" : "border-gray-300"
              }`}
            >
              <option value="">Select value...</option>
              {firstOptions.map((opt) => (
                <option key={opt.id} value={opt.id}>
                  {`${opt.description_es} (${opt.id})`}
                </option>
              ))}
            </select>
          ) : (
            <input
              type="text"
              id="firstValue"
              className={`w-full border rounded-lg p-2.5 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                errors.firstValue ? "border-red-500" : "border-gray-300"
              }`}
              placeholder="Enter first value..."
              {...register("firstValue")}
            />
          )}
          {errors.firstValue && (
            <p className="text-red-500 text-xs mt-1">
              {errors.firstValue.message}
            </p>
          )}
        </div>

        {/* Second Value with Checkbox */}
        <div>
          <label
            htmlFor="secondValue"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            Second Value {!useFirstVariableValue && <span className="text-red-500">*</span>}
          </label>
          
          {/* Use First Value Checkbox */}
          <div className="mb-2">
            <label className="flex items-center space-x-2 cursor-pointer">
              <Controller
                name="useFirstVariableValue"
                control={control}
                render={({ field }) => (
                  <input
                    type="checkbox"
                    checked={field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                )}
              />
              <span className="text-sm text-gray-700">Use first variable value</span>
            </label>
          </div>

          {isLoadingSecondOptions && !useFirstVariableValue ? (
            <div className="w-full border border-gray-300 rounded-lg p-2.5 bg-gray-50 flex items-center justify-center">
              <span className="text-gray-500 text-sm">Loading options...</span>
            </div>
          ) : secondOptions.length > 0 && !useFirstVariableValue ? (
            <select
              id="secondValue"
              {...register("secondValue")}
              className={`w-full border rounded-lg p-2.5 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                errors.secondValue ? "border-red-500" : "border-gray-300"
              }`}
            >
              <option value="">Select value...</option>
              {secondOptions.map((opt) => (
                <option key={opt.id} value={opt.id}>
                  {`${opt.description_es} (${opt.id})`}
                </option>
              ))}
            </select>
          ) : (
            <input
              type="text"
              id="secondValue"
              placeholder={
                useFirstVariableValue
                  ? "Using first variable value"
                  : "Enter second value..."
              }
              className={`w-full border rounded-lg p-2.5 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                useFirstVariableValue ? "bg-gray-100 cursor-not-allowed" : ""
              } ${errors.secondValue ? "border-red-500" : "border-gray-300"}`}
              disabled={useFirstVariableValue}
              {...register("secondValue")}
            />
          )}
          {errors.secondValue && (
            <p className="text-red-500 text-xs mt-1">
              {errors.secondValue.message}
            </p>
          )}
        </div>

        {/* Error Message */}
        <div className="md:col-span-2">
          <label
            htmlFor="errorMessage"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            Error Message <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="errorMessage"
            className={`w-full border rounded-lg p-2.5 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
              errors.errorMessage ? "border-red-500" : "border-gray-300"
            }`}
            placeholder="Enter error message to display when rule fails..."
            {...register("errorMessage")}
          />
          {errors.errorMessage && (
            <p className="text-red-500 text-xs mt-1">
              {errors.errorMessage.message}
            </p>
          )}
        </div>

        {/* Applicable Phases */}
        <div className="md:col-span-2">
          <Controller
            name="applicablePhaseIds"
            control={control}
            render={({ field }) => (
              <PhaseSelector
                selectedPhases={selectedPhases || []}
                onChange={field.onChange}
                error={errors.applicablePhaseIds?.message}
              />
            )}
          />
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end gap-3 pt-4 border-t">
        <button
          type="button"
          onClick={handleReset}
          className="px-6 py-2.5 text-sm font-medium text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-400 transition"
        >
          Reset
        </button>
        <button
          type="submit"
          className="px-6 py-2.5 text-sm font-medium text-white bg-black rounded-lg hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-600 transition"
        >
          Create Rule
        </button>
      </div>
    </form>
  );
};

export default CausalityRuleForm;